import type { TFunction } from 'i18next';
import { toJS } from 'mobx';

import type {
  Campaign,
  IEmailABTest,
  IMailrailSingleSendMailing,
  MailrailSingleSendEmailCampaign,
  TEmailABTestVariation,
  TFlagsRecord,
} from '@feathr/blackbox';
import {
  EEmailABTestStatus,
  EEmailABTestWinningMetric,
  EMAIL_AB_TEST_DEFAULTS,
  EMAIL_AB_TEST_VARIATIONS,
} from '@feathr/blackbox';
import type { IUniversalSelectOption, TTheme, TThemeShade } from '@feathr/components';
import { ETheme } from '@feathr/components';

export interface IEmailABTestColor {
  shade: TThemeShade;
  theme: TTheme;
}

export const EMAIL_AB_TEST_VERSION_COLORS: Readonly<
  Record<TEmailABTestVariation, IEmailABTestColor>
> = {
  a: { shade: 600, theme: ETheme.green },
  b: { shade: 500, theme: ETheme.sky },
};

export function isEmailABTestAvailable(flags: Partial<TFlagsRecord>, campaign: Campaign): boolean {
  return Boolean(flags.showEmailABTesting) && campaign.isMailrailSingleSend;
}

export function getEmailABTest(campaign: MailrailSingleSendEmailCampaign): IEmailABTest {
  return { ...EMAIL_AB_TEST_DEFAULTS, ...toJS(campaign.get('ab_test')) };
}

// Later letters map to variant mailings in list order.
export function getEmailABTestVariationMailing(
  campaign: MailrailSingleSendEmailCampaign,
  variation: TEmailABTestVariation,
): IMailrailSingleSendMailing | undefined {
  return campaign.getVariantMailings()[EMAIL_AB_TEST_VARIATIONS.indexOf(variation) - 1];
}

export function isEmailABTestResolved({ status }: IEmailABTest): boolean {
  return (
    status === EEmailABTestStatus.ResolvedWinner || status === EEmailABTestStatus.ResolvedFallback
  );
}

// The version a resolved test sent to the rest of the audience: the winner, or the fallback when no version won.
export function getEmailABTestSentVariation(
  abTest: IEmailABTest,
): TEmailABTestVariation | undefined {
  if (abTest.status === EEmailABTestStatus.ResolvedWinner) {
    return abTest.winner_variation;
  }
  return abTest.status === EEmailABTestStatus.ResolvedFallback
    ? abTest.fallback_variation
    : undefined;
}

export function getEmailABTestVersionLabel(variation: TEmailABTestVariation, t: TFunction): string {
  return t('Version {{letter}}', { letter: variation.toUpperCase() });
}

export function getVariationOptions(
  t: TFunction,
): Array<IUniversalSelectOption<TEmailABTestVariation>> {
  return EMAIL_AB_TEST_VARIATIONS.map((variation) => ({
    id: variation,
    name: getEmailABTestVersionLabel(variation, t),
  }));
}

export function getWinningMetricOptions(
  t: TFunction,
): Array<IUniversalSelectOption<EEmailABTestWinningMetric>> {
  return [
    { id: EEmailABTestWinningMetric.Opens, name: t('Open rate') },
    { id: EEmailABTestWinningMetric.Clicks, name: t('Click rate') },
    { id: EEmailABTestWinningMetric.Conversions, name: t('Conversions') },
  ];
}

export function getWinningMetricHelpText(metric: EEmailABTestWinningMetric, t: TFunction): string {
  const helpTextMap: Record<EEmailABTestWinningMetric, string> = {
    [EEmailABTestWinningMetric.Clicks]: t('The version with more unique clicks wins.'),
    [EEmailABTestWinningMetric.Conversions]: t('The version with more conversions wins.'),
    [EEmailABTestWinningMetric.Opens]: t('The version with more unique opens wins.'),
  };
  return helpTextMap[metric];
}
